import {
  getLocalTags,
  resetBagPickupMode,
  setGroupSpecialSelection,
} from '@iheartjane/cart/utils';
import { trackBranchCheckout } from '@iheartjane/tracking';
import type { AppMode, PriceId, ReservationMode } from '@iheartjane/types';
import { shopperTimeZone } from '@iheartjane/utils';
import type { NavigateFunction } from 'react-router';

import { trackBranchIoError } from '@jane/dm/internal';
import { trackCheckoutSuccess } from '@jane/monolith/customer/cart';
import { NotificationsService } from '@jane/monolith/services/notifications';
import type { MoeEvent } from '@jane/shared-ecomm/tracking';
import { moeCheckout } from '@jane/shared-ecomm/tracking';
import { postCheckoutAnalyticsEvent } from '@jane/shared-ecomm/util';
import type { CartLineItems } from '@jane/shared/types';
import type { WeightPriced } from '@jane/shared/util';
import { formatTime, monolithPaths as paths } from '@jane/shared/util';

type TrackedCart = Parameters<typeof trackCheckoutSuccess>[0]['cart'];
type PostedCart = Parameters<typeof postCheckoutAnalyticsEvent>[0]['cart'];
type MoeCart = Parameters<typeof moeCheckout>[0]['cart'];

interface CheckoutSuccessCartProduct extends WeightPriced {
  aggregate_rating?: number | null;
  brand?: string | null;
  brand_subtype?: string | null;
  category?: string | null;
  count: number;
  id: number | string;
  kind: string;
  name: string;
  price_id: PriceId;
  product_brand_id?: number | null;
  product_id: number | string;
  review_count?: number | null;
  root_subtype?: string | null;
  special_id?: number | string | null;
  special_title?: string | null;
}

/** The fields the checkout events read; v1 and v3 carts both fit. */
export interface CheckoutSuccessCart {
  brand_discounts_total_with_fee?: number | null;
  cart_discount_amount?: number | null;
  delivery_fee_amount?: number | null;
  id?: number | string | null;
  item_total?: number | null;
  order_total?: number | null;
  products: readonly CheckoutSuccessCartProduct[];
  reservation_mode: ReservationMode;
  sales_tax_amount?: number | null;
  service_fee_amount?: number | null;
  store?: { id: number | string } | null;
  uuid: string;
}

export interface CheckoutSuccessResult {
  contact?: { is_registered: boolean } | null;
  message: string;
  reservation: {
    amount: string | number | null;
    delivery_finished_at: number | null;
    delivery_started_at: number | null;
    id: number;
    message: string | null;
    payment_method: string | null;
    uuid: string;
  };
  warning_message?: string | null;
}

export interface CheckoutSuccessEffectsProps {
  appMode: AppMode | undefined;
  /** The cart as it was before checkout, which empties it. */
  cart: CheckoutSuccessCart;
  /** The checkout page's line items; the tip lives here, not on the cart. */
  cartLineItems: CartLineItems;
  customer: {
    birth_date: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
  deliveryAddress: Parameters<
    typeof postCheckoutAnalyticsEvent
  >[0]['deliveryAddress'];
  isEmbeddedMode: boolean;
  isGuest: boolean;
  isStackAdaptPixelEnabled: boolean;
  moeTrack: (event: MoeEvent) => void;
  navigate: NavigateFunction;
  partnerId: string | number | undefined;
  paymentMethod?: string;
  result: CheckoutSuccessResult;
  store: Parameters<typeof trackCheckoutSuccess>[0]['store'] | null;
}

const formatReservationTime = (
  time: number | null,
  timezone: Parameters<typeof formatTime>[0]['timezone']
) =>
  time
    ? formatTime({ time, outputFormat: '', inputFormat: 't', timezone })
    : undefined;

export const runCheckoutSuccessEffects = ({
  appMode,
  cart,
  cartLineItems,
  customer: { birth_date, email, firstName, lastName, phone },
  deliveryAddress,
  isEmbeddedMode,
  isGuest,
  isStackAdaptPixelEnabled,
  moeTrack,
  navigate,
  partnerId,
  paymentMethod,
  result: { contact, message, reservation, warning_message },
  store,
}: CheckoutSuccessEffectsProps) => {
  trackBranchCheckout(cart.uuid, trackBranchIoError);

  const tz = store?.time_zone_identifier ?? shopperTimeZone();
  const delivery_finished_at = formatReservationTime(
    reservation.delivery_finished_at,
    tz
  );
  const delivery_started_at = formatReservationTime(
    reservation.delivery_started_at,
    tz
  );

  // The consumers' cart types are v1-only; they read only the fields above.
  postCheckoutAnalyticsEvent({
    cart: {
      ...cart,
      tags: getLocalTags(appMode, partnerId),
      delivery_finished_at,
      delivery_started_at,
      message: reservation.message,
      payment_method: reservation.payment_method,
    } as unknown as PostedCart,
    cartLineItems: {
      ...cartLineItems,
      total: Number(reservation.amount),
    },
    isStackAdaptPixelEnabled,
    deliveryAddress,
    orderCustomer: {
      birthDate: birth_date,
      email,
      firstName,
      lastName,
      phone,
    },
  });

  moeTrack(
    moeCheckout({
      cart: {
        ...cart,
        tip_amount: String(cartLineItems.tipAmount),
      } as unknown as MoeCart,
      janePayStore: store ? !!store.jane_pay_integration?.enabled : false,
      storeId: store?.id ?? null,
      storeName: store?.name ?? null,
      paymentType: reservation.payment_method,
      reservationTime: delivery_started_at
        ? new Date(delivery_started_at)
        : undefined,
    })
  );

  trackCheckoutSuccess({
    cart: cart as unknown as TrackedCart,
    paymentMethod,
    store: store || undefined,
  });

  if (!warning_message) {
    NotificationsService.success(message);
  }

  if (isGuest) {
    navigate(
      paths.guestReservationReceipt(reservation.uuid, {
        is_registered: contact?.is_registered,
      })
    );
  } else {
    const urlQuery: Record<string, unknown> = {};
    if (warning_message) {
      urlQuery['warning_message'] = warning_message;
    }
    navigate(
      isEmbeddedMode
        ? paths.embeddedReservation(reservation.id, urlQuery)
        : paths.reservation(reservation.id, urlQuery)
    );
  }

  resetBagPickupMode();
  setGroupSpecialSelection(cart.uuid, null);
};
